<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Chart, ChartConfiguration, TooltipItem } from 'chart.js/auto'
import { forEach, groupBy } from 'lodash'
import dayjs from 'dayjs'
import { formatMoneyWithSymbol } from '@/lib/money'
import type { CatalogItemSnapshot, ChartHistoricalPoint } from './catalogDetailTypes'

/**
 * One chart, one currency.
 *
 * Every series here is denominated in `currencySymbol`, so the shared y-axis is a
 * real comparison. Plotting a second currency on it would draw two different units
 * as one line of reasoning — which is why the caller partitions before rendering and
 * this component never groups.
 */
const props = defineProps<{
    historical: ChartHistoricalPoint[]
    /** The customer's own price history, already narrowed to this chart's currency. */
    snapshots: CatalogItemSnapshot[]
    currencySymbol: string
    ownPriceLabel: string
}>()

const canvas = ref<HTMLCanvasElement | null>(null)
let chart: Chart | null = null

type Dataset = {
    label: string
    data: { x: number; y: number }[]
    pointRadius?: number
}

const buildDatasets = (): Dataset[] => {
    const datasets: Dataset[] = []

    forEach(groupBy(props.historical, 'vendor_name'), (prices, vendor) => {
        datasets.push({
            label: vendor.replace(/[[\]]/g, ''),
            data: prices.map((price) => ({
                x: dayjs(price.date).unix(),
                y: price.median_price,
            })),
        })
    })

    // A snapshot is taken of every item every batch, price or not (ADR-008), so the
    // price-less ones are dropped here rather than plotted at zero.
    const ownPrices = props.snapshots.flatMap((snapshot) =>
        snapshot.price === null ? [] : [{ x: dayjs(snapshot.created_at).unix(), y: snapshot.price }],
    )

    // ADR-016: the customer's own price is a comparison drawn alongside the market,
    // never merged into it. It is labelled as a selling price on both charts so the
    // trade-in one cannot be read as an offer they make.
    if (ownPrices.length > 0) {
        datasets.push({
            label: props.ownPriceLabel,
            data: ownPrices,
            pointRadius: 7,
        })
    }

    return datasets
}

const render = () => {
    if (canvas.value === null) {
        return
    }

    // Updated in place when the canvas already carries a chart: narrowing one
    // currency's country chip re-runs every sibling's props, and a teardown per
    // chart re-attaches the canvas, rebinds listeners and replays the entry
    // animation for charts whose data did not change.
    if (chart !== null) {
        chart.data.datasets = buildDatasets()
        chart.update()

        return
    }

    const config: ChartConfiguration<'scatter'> = {
        type: 'scatter',
        data: { datasets: buildDatasets() },
        options: {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                    title: { color: 'rgba(156, 163, 175, 1)' },
                },
                tooltip: {
                    callbacks: {
                        title: (items: TooltipItem<'scatter'>[]) => {
                            const day = dayjs.unix(Number(items[0].parsed.x)).format('DD/MM')

                            return items[0].dataset.label + ' ' + day
                        },
                        label: (item: TooltipItem<'scatter'>) =>
                            formatMoneyWithSymbol(item.parsed.y, props.currencySymbol),
                    },
                },
            },
            scales: {
                y: {
                    border: { display: false },
                    grid: { color: 'rgba(156, 163, 175, 0.2)' },
                    ticks: { color: 'rgba(156, 163, 175, 1)' },
                },
                x: {
                    border: { display: false },
                    grid: { color: 'rgba(156, 163, 175, 0.2)' },
                    ticks: {
                        callback: (value: string | number) => dayjs.unix(Number(value)).format('DD/MM'),
                        color: 'rgba(156, 163, 175, 1)',
                    },
                },
            },
        },
    }

    chart = new Chart(canvas.value, config)
}

onMounted(render)

watch(() => [props.historical, props.snapshots], render)

onBeforeUnmount(() => {
    chart?.destroy()
    chart = null
})
</script>

<template>
    <canvas ref="canvas" />
</template>
